import { ChallengeService } from '@duck/api-definitions'
import { useSetAtom } from 'jotai'
import { WorkspaceHeaderPane } from './WorkspaceHeaderPane'
import { MobileWorkspace } from './MobileWorkspace'
import { cn, useIsMobile, useOnMobileDesktopChange, type MobileDesktopChangeParams } from '@duck/ui'
import { useCallback, useLayoutEffect } from 'react'
import { WorkspaceStatusPane } from './WorkspaceStatusPane'
import { workspaceSolutionHandlersAtom } from '@/store/workspaceAtoms'
import { useEventEmitter } from '@/modules/EventBusModule'
import { WorkspaceTabs } from '../WorkspaceTabs/WorkspaceTabs'
import { WorkspaceSettingsDialog } from './WorkspaceSettings/WorkspaceSettingsDialog'
import { useWorkspaceSettingsShortcut } from './WorkspaceSettings/useWorkspaceSettingsShortcut'
import { useShortcutsDialogShortcut } from './WorkspaceSettings/useShortcutsDialogShortcut'
import { useShortcutMatcher, useHeldModifiers } from '@/modules/Shortcuts'

interface WorkspaceProps {
  challenge: ChallengeService.ChallengeInfo['challenge'];
  task: ChallengeService.ChallengeInfo['task'];
  charts: ChallengeService.WorkState['charts'];
  challengeId: string;
  workTime: number;
  handleSolutionChange: (solution: string) => void;
  handleChartAdded: (solution: string, chart: ChallengeService.Chart) => void;
  handleChartRemoved: (solution: string, remainingChartIds: string[]) => void;
}

export const Workspace = ({
  challenge,
  task,
  charts,
  challengeId,
  workTime,
  handleSolutionChange,
  handleChartAdded,
  handleChartRemoved,
}: WorkspaceProps) => {
  const isMobile = useIsMobile()
  const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw', 10)
  useWorkspaceSettingsShortcut()
  useShortcutsDialogShortcut()
  useShortcutMatcher()
  useHeldModifiers()

  const handleMobileDesktopChange = useCallback(
    ({ wasMobile, isMobile: nowMobile }: MobileDesktopChangeParams) => {
      if (wasMobile && !nowMobile) {
        emitChartsTriggerRedraw({})
      }
    },
    [emitChartsTriggerRedraw]
  )
  useOnMobileDesktopChange(handleMobileDesktopChange)
  const setSolutionHandlers = useSetAtom(workspaceSolutionHandlersAtom)

  useLayoutEffect(() => {
    setSolutionHandlers({
      handleSolutionChange,
      handleChartAdded,
      handleChartRemoved,
    })
    return () => {
      setSolutionHandlers(null)
    }
  }, [handleSolutionChange, handleChartAdded, handleChartRemoved, setSolutionHandlers])

  return (
    <>
      {/* Desktop layout - hidden when mobile. Keep mounted so WorkspaceTabs state persists on resize. */}
      <div
        className={cn(
          'flex flex-col flex-1 relative overflow-hidden h-screen',
          isMobile && 'hidden'
        )}
        data-tour-id="challenge-env"
      >
        <WorkspaceHeaderPane challenge={challenge} task={task} workTime={workTime} />

        <div className='flex-1 min-h-0 border-b border-border flex flex-col'>
          <WorkspaceTabs
            challenge={challenge}
            challengeId={challengeId}
            defaultQuery={task.defaultQuery ?? ''}
          />
        </div>

        {!challenge.isSandbox && (
          <WorkspaceStatusPane challenge={challenge} hasCharts={Object.keys(charts || {}).length > 0} />
        )}
      </div>

      {/* Mobile layout - only when mobile */}
      {isMobile && (
        <MobileWorkspace
          challenge={challenge}
          task={task}
          charts={charts}
          challengeId={challengeId}
          workTime={workTime}
          handleSolutionChange={handleSolutionChange}
          handleChartAdded={handleChartAdded}
          handleChartRemoved={handleChartRemoved}
        />
      )}

      <WorkspaceSettingsDialog />
    </>
  )
}
